import React, { useState } from 'react';
import { Shield, Smartphone, Copy, Check, Loader2, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { API } from '../../lib/axiosClient';

export const TwoFactorSetup = ({ onComplete }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState('idle');
  const [qrData, setQrData] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/2fa/setup`);
      setQrData(res.data);
      setStep('qr');
    } catch (e) {
      setError(e.response?.data?.detail || e.message || t('twoFactor.setupFailed'));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (code.length !== 6) { setError(t('twoFactor.enter6DigitError')); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/2fa/verify`, { code });
      setBackupCodes(res.data.backup_codes || []);
      setStep('backup');
    } catch (e) {
      setError(e.response?.data?.detail || e.message || t('twoFactor.verificationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (step === 'idle') {
    return (
      <div className="space-y-4" data-testid="2fa-setup-idle">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-5 h-5 text-emerald-500" />
          <span className="font-semibold text-sm">{t('twoFactor.title')}</span>
        </div>
        <p className="text-xs text-slate-400">{t('twoFactor.protectAccountDesc')}</p>
        <button onClick={startSetup} disabled={loading} data-testid="2fa-setup-start-btn"
          className="w-full py-2 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
          {t('twoFactor.enable')}
        </button>
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    );
  }

  if (step === 'qr') {
    return (
      <div className="space-y-4" data-testid="2fa-setup-qr">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-5 h-5 text-amber-500" />
          <span className="font-semibold text-sm">{t('twoFactor.scanQr')}</span>
        </div>
        <p className="text-xs text-slate-400">{t('twoFactor.scanQrInstruction')}</p>
        {qrData?.qr_code && (
          <div className="flex justify-center p-4 bg-white rounded-lg">
            <img src={qrData.qr_code} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code-img" />
          </div>
        )}
        <div className="text-center">
          <p className="text-xs text-slate-500 mb-1">{t('twoFactor.manualEntryLabel')}</p>
          <code className="text-xs bg-slate-800 px-3 py-1 rounded select-all break-all">{qrData?.secret}</code>
        </div>
        <div className="space-y-2">
          <label className="text-xs text-slate-400">{t('twoFactor.codeFromAppLabel')}</label>
          <input type="text" maxLength={6} value={code}
            onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
            placeholder="000000"
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-center text-lg tracking-[0.5em] font-mono focus:border-emerald-500 focus:outline-none"
            autoFocus data-testid="2fa-code-input" />
        </div>
        <button onClick={verifyCode} disabled={loading || code.length !== 6} data-testid="2fa-verify-btn"
          className="w-full py-2 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {t('twoFactor.verifyCode')}
        </button>
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    );
  }

  if (step === 'backup') {
    return (
      <div className="space-y-4" data-testid="2fa-setup-backup">
        <div className="flex items-center gap-3 mb-2">
          <Check className="w-5 h-5 text-emerald-500" />
          <span className="font-semibold text-sm">{t('twoFactor.activated')}</span>
        </div>
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-semibold text-amber-400">{t('twoFactor.saveBackupCodesAlert')}</span>
          </div>
          <p className="text-xs text-slate-400">{t('twoFactor.saveCodesHint')}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 p-3 bg-slate-800 rounded-lg font-mono text-xs">
          {backupCodes.map((c, i) => (
            <div key={`bc-${i}-${c}`} className="px-2 py-1 bg-slate-700 rounded text-center">{c}</div>
          ))}
        </div>
        <button onClick={copyBackupCodes} data-testid="2fa-copy-backup-btn"
          className="w-full py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors flex items-center justify-center gap-2">
          {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          {copied ? t('twoFactor.copiedBtn') : t('twoFactor.copyAllCodesBtn')}
        </button>
        <button onClick={() => { onComplete?.(); }} data-testid="2fa-setup-done-btn"
          className="w-full py-2 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-colors">
          Fertig
        </button>
      </div>
    );
  }

  return null;
};
